import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import classNames, { Argument } from "classnames/dedupe";

import { CounterBadge } from "../../../Atoms/CounterBadge";
import { DownIconSmall } from "../../../../icons";
import { DropdownContext } from "../../../Utility/Dropdown/Dropdown.context";
import { SvgIcon } from "../../../Icons";

export interface IFilterDropdownButtonProps {
  /**
   * Strategy of opening (by hover, click, etc.)
   * */
  variant?: "hover" | "click";
  /** Text of button */
  title: string;
  /** Value of badge */
  badgeCount?: number | string;
  classes?: {
    root?: Argument;
    button?: Argument;
  };
  children?: ReactNode;
  onClick?(e: MouseEvent): void;
  onMouseEnter?(e: MouseEvent): void;
  onMouseLeave?(e: MouseEvent): void;
}

export const FilterDropdownButton = forwardRef<
  HTMLDivElement,
  IFilterDropdownButtonProps
>(
  (
    {
      classes,
      variant = "hover",
      title,
      badgeCount,
      children,
      onClick,
      ...other
    },
    ref,
  ) => {
    const { opened, setOpened } = useContext(DropdownContext);

    const handleClick = useCallback(
      (e: MouseEvent) => {
        if (variant === "click") setOpened(!opened);
        onClick && onClick(e);
      },
      [onClick, opened, setOpened, variant],
    );

    return (
      <div
        data-testid="FilterDropdownButton"
        ref={ref}
        className={classNames("dropdown", classes?.root)}
        onClick={handleClick}
        {...other}
      >
        <button
          className={classNames(
            "dropdown-toggle bg-color-white",
            classes?.button,
          )}
          data-bs-toggle="dropdown"
          type="button"
          id={title}
          aria-expanded="false"
        >
          {title}
          {badgeCount ? (
            <CounterBadge count={badgeCount} classes={{ root: "ms-3" }} />
          ) : (
            <div
              className="icon-container dropdown-toggle__icon"
              data-testid="filterDropdown-icon"
            >
              <SvgIcon
                icon={DownIconSmall}
                classes={{
                  root: classNames("uds-rotate", {
                    "uds-rotate--180-counter-clockwise": opened,
                  }),
                }}
              />
            </div>
          )}
        </button>
        {children}
      </div>
    );
  },
);

export default FilterDropdownButton;
